/**
 * TOSS Web Application - Platform Detection Utility
 *
 * Helper for detecting the runtime platform (browser / PWA / mobile)
 * for attaching platform metadata to FCM device tokens.
 *
 * Used by push notification registration to differentiate between
 * desktop browsers, mobile browsers, and installed PWAs on Android/iOS.
 */

/**
 * Detect the runtime platform.
 *
 * Return values:
 * - `android-pwa` — Android browser running in PWA standalone mode
 * - `ios-pwa` — iOS Safari running as an installed PWA
 * - `web` — Android or iOS browser (not standalone)
 * - `desktop` — desktop browser (Windows, macOS, Linux)
 */
export function detectPlatform(): string {
  const ua = navigator.userAgent.toLowerCase();

  // `display-mode: standalone` — PWA is running as an installed app.
  // iOS Safari exposes a non-standard `navigator.standalone` property.
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  if (/android/.test(ua)) {
    return isStandalone ? 'android-pwa' : 'web';
  }

  if (/iphone|ipad|ipod/.test(ua)) {
    return isStandalone ? 'ios-pwa' : 'web';
  }

  return 'desktop';
}
